import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './PublicSiteHeader.css';
import MarketingImage from './MarketingImage';
import { navigateToPage } from '../utils/navigation';

const PRIMARY_NAV_ITEMS = [
  { label: 'Features', page: 'features', activePages: ['features'] },
  { label: 'Pricing', page: 'pricing', activePages: ['pricing'] },
  { label: 'About', page: 'about', activePages: ['about'] },
  { label: 'Help', page: 'help', activePages: ['help', 'faq', 'cookies', 'terms', 'privacy', 'verify', 'sitemap'] },
  { label: 'Contact', page: 'contact', activePages: ['contact'] },
];

const SECONDARY_MENU_ITEMS = [
  { label: 'FAQ', page: 'faq' },
  { label: 'Terms', page: 'terms' },
  { label: 'Privacy', page: 'privacy' },
  { label: 'Cookies', page: 'cookies' },
  { label: 'Sitemap', page: 'sitemap' },
  { label: 'Verify Certificate', page: 'verify' },
];

function isCurrentPage(item, currentPage) {
  return item.activePages.includes(currentPage);
}

function PublicSiteHeader({
  currentPage = 'home',
  onPrimaryAction,
  primaryActionLabel = 'Start Free Trial',
  onSecondaryAction,
  secondaryActionLabel,
  showSecondaryAction = true,
  tone = 'default',
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({
    top: 84,
    right: 16,
    left: 16,
    maxHeight: 640,
  });
  const headerRef = useRef(null);
  const shellRef = useRef(null);

  useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalOverflow = document.body.style.overflow;
    const originalTouchAction = document.body.style.touchAction;

    if (mobileMenuOpen) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    }

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalOverflow;
      document.body.style.touchAction = originalTouchAction;
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [currentPage]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (tone !== 'home') {
      return undefined;
    }

    let resizeObserver;

    const updateHeaderHeight = () => {
      const headerHeight = headerRef.current?.offsetHeight;

      if (!headerHeight) {
        return;
      }

      document.documentElement.style.setProperty('--public-site-home-header-height', `${headerHeight}px`);
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    if (typeof ResizeObserver === 'function' && headerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        updateHeaderHeight();
      });
      resizeObserver.observe(headerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
      resizeObserver?.disconnect();
      document.documentElement.style.removeProperty('--public-site-home-header-height');
    };
  }, [tone]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      return undefined;
    }

    const updateMenuPosition = () => {
      if (typeof window === 'undefined') {
        return;
      }

      const shellRect = shellRef.current?.getBoundingClientRect();
      const horizontalPadding = window.innerWidth <= 560 ? 12 : 16;
      const rightOffset = shellRect
        ? Math.max(horizontalPadding, window.innerWidth - shellRect.right)
        : horizontalPadding;
      const topOffset = shellRect
        ? Math.max(shellRect.bottom + 12, 72)
        : 84;

      setMenuPosition({
        top: topOffset,
        right: rightOffset,
        left: horizontalPadding,
        maxHeight: Math.max(220, window.innerHeight - topOffset - horizontalPadding),
      });
    };

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, { passive: true });

    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition);
    };
  }, [mobileMenuOpen]);

  const secondaryLabel = secondaryActionLabel || (currentPage === 'pricing'
    ? 'Explore Features'
    : 'View Pricing');

  const handlePrimaryAction = () => {
    setMobileMenuOpen(false);

    if (onPrimaryAction) {
      onPrimaryAction();
      return;
    }

    navigateToPage('pricing');
  };

  const handleSecondaryAction = () => {
    setMobileMenuOpen(false);

    if (onSecondaryAction) {
      onSecondaryAction();
      return;
    }

    navigateToPage(currentPage === 'pricing' ? 'features' : 'pricing');
  };

  const handleNavigation = (page) => {
    setMobileMenuOpen(false);
    navigateToPage(page);
  };

  const mobileMenu = mobileMenuOpen && typeof document !== 'undefined'
    ? createPortal(
      <div
        className="public-site-header__menu-backdrop"
        role="presentation"
        onClick={() => setMobileMenuOpen(false)}
        style={{
          paddingTop: `${menuPosition.top}px`,
          paddingRight: `${menuPosition.right}px`,
          paddingLeft: `${menuPosition.left}px`,
        }}
      >
        <div
          id="public-site-mobile-menu"
          className="public-site-header__menu-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="public-site-mobile-menu-title"
          onClick={(event) => event.stopPropagation()}
          style={{
            maxHeight: `${menuPosition.maxHeight}px`,
          }}
        >
          <div className="public-site-header__menu-copy">
            <p id="public-site-mobile-menu-title" className="public-site-header__menu-title">
              Explore TryInterview
            </p>
            <p className="public-site-header__menu-text">
              Move between the platform pages, pricing, support, and certificate verification without
              losing the flow.
            </p>
          </div>

          <div className="public-site-header__menu-grid">
            {PRIMARY_NAV_ITEMS.map((item) => (
              <a
                key={item.page}
                href={`/${item.page}/`}
                className={`public-site-header__menu-link${isCurrentPage(item, currentPage) ? ' is-active' : ''}`}
                onClick={(event) => { event.preventDefault(); handleNavigation(item.page); }}
              >
                <span>{item.label}</span>
                <span aria-hidden="true">›</span>
              </a>
            ))}
          </div>

          <div className="public-site-header__menu-utility">
            {SECONDARY_MENU_ITEMS.map((item) => (
              <a
                key={item.page}
                href={`/${item.page}/`}
                className={`public-site-header__utility-link${currentPage === item.page ? ' is-active' : ''}`}
                onClick={(event) => { event.preventDefault(); handleNavigation(item.page); }}
              >
                {item.label}
              </a>
            ))}
          </div>

          <div className="public-site-header__menu-actions">
            {showSecondaryAction && (
              <button
                type="button"
                className="public-site-header__action public-site-header__action--secondary public-site-header__action--mobile"
                onClick={handleSecondaryAction}
              >
                {secondaryLabel}
              </button>
            )}
            <button
              type="button"
              className="public-site-header__action public-site-header__action--primary public-site-header__action--mobile"
              onClick={handlePrimaryAction}
            >
              {primaryActionLabel}
            </button>
          </div>
        </div>
      </div>,
      document.body,
    )
    : null;

  return (
    <>
      <header
        ref={headerRef}
        className={`public-site-header public-site-header--${tone}${mobileMenuOpen ? ' public-site-header--menu-open' : ''}`}
      >
        <div ref={shellRef} className="public-site-header__shell">
          <a
            href="/"
            className="public-site-header__brand"
            onClick={(event) => { event.preventDefault(); handleNavigation('home'); }}
            aria-label="Go to TryInterview home"
          >
            <span className="public-site-header__brand-mark">
              <MarketingImage
                src="/logo-icon.svg"
                alt="TryInterview logo"
                className="public-site-header__brand-logo"
                priority={tone === 'home'}
              />
            </span>
            <span className="public-site-header__brand-copy">
              <span className="public-site-header__brand-row">
                <span className="public-site-header__brand-name">TryInterview</span>
                <span className="public-site-header__brand-badge">Interview Prep Platform</span>
              </span>
              <span className="public-site-header__brand-tagline">
                Realistic AI practice, sharper feedback, and better answers.
              </span>
            </span>
          </a>

          <nav className="public-site-header__nav" aria-label="Primary navigation">
            {PRIMARY_NAV_ITEMS.map((item) => (
              <a
                key={item.page}
                href={`/${item.page}/`}
                className={`public-site-header__nav-link${isCurrentPage(item, currentPage) ? ' is-active' : ''}`}
                onClick={(event) => { event.preventDefault(); handleNavigation(item.page); }}
                aria-current={isCurrentPage(item, currentPage) ? 'page' : undefined}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="public-site-header__actions">
            {showSecondaryAction && (
              <button
                type="button"
                className="public-site-header__action public-site-header__action--secondary"
                onClick={handleSecondaryAction}
              >
                {secondaryLabel}
              </button>
            )}
            <button
              type="button"
              className="public-site-header__action public-site-header__action--primary"
              onClick={handlePrimaryAction}
            >
              {primaryActionLabel}
            </button>
          </div>

          <button
            type="button"
            className="public-site-header__menu-button"
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls={mobileMenuOpen ? 'public-site-mobile-menu' : undefined}
            onClick={() => setMobileMenuOpen((previousValue) => !previousValue)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>
      {mobileMenu}
    </>
  );
}

export default PublicSiteHeader;
